import { LOCALES } from '../../common/constants';

/**
 * Utility functions for route localization
 */

interface RouteMapping {
  [key: string]: string;
}

interface RouteMappings {
  'sv-en': RouteMapping;
  'en-sv': RouteMapping;
}

/**
 * Route mapping between languages
 * Maps Swedish routes to English routes and vice versa
 */
export const routeMapping: RouteMappings = {
  // Swedish to English
  'sv-en': {
    '/funktion': '/function',
    '/hur-fungerar-invozio': '/how-works-invozio',
    '/om-invozio': '/about-invozio',
    '/kontakta-oss': '/contact-us',
    '/starta-eget-foretag-eller-fakturera-som-privatperson': '/start-your-own-business-or-invoice-as-a-private-person',
    '/instrumentbrada': '/dashboard',
    '/forsakring': '/insurance',
    '/sjukforsakring-huvud': '/health-insurance-main',
    '/sjukforsakring': '/health-insurance',
    '/privatvardsforsakring': '/private-healthcare-insurance',
    '/frilans-konsult-lon-fakturera-utan-foretag': '/freelance-consultant-salary-invoice-without-a-company',
    '/fakturering-utan-foretag-2025': '/invoicing-without-company-2025',
    '/att-jobba-som-frilansare-och-fakturera-utan-fskatt': '/working-as-a-freelancer-and-invoicing-without-tax-registration',
    '/hur-mycket-ska-jag-fakturera-som-frilansare': '/how-much-should-I-invoice-as-a-freelancer',
    '/fran-faktura-till-forhandling': '/from-invoice-to-negotiation',
    '/foretag': '/business',
    '/egenanstallning': '/self-employment',
    '/avprenumererad': '/unsubscribed',
    '/auktorisera-påminnelse': '/authorize-reminder',
    '/godkannande-meddelande': '/approval-notify',
    '/frilans-frilansarbete': '/freelance-freelancing',
    '/fakturera-som-privatperson_egenanstalld': '/invoicing-as-an-individual_self-employed',
    '/fakturera-utan-foretag-eller-bolag': '/invoice-without-company-or-business',
    '/fakturera-som-konsult-lon': '/invoice-as-consultant-salary',
    '/nyheter': '/news',
    '/bokforing': '/book-keeping',
    '/frilansguide': '/freelancing-guide',
    '/e-postregister': '/emailregister',
    '/e-postinloggning': '/emaillogin',
    '/glomt-ditt-losenord': '/forgot-password',
    '/logga-in': '/login',
    '/set-nytt-losenord': '/set-new-password',
  },
  // English to Swedish
  'en-sv': {
    '/function': '/funktion',
    '/how-works-invozio': '/hur-fungerar-invozio',
    '/about-invozio': '/om-invozio',
    '/contact-us': '/kontakta-oss',
    '/start-your-own-business-or-invoice-as-a-private-person': '/starta-eget-foretag-eller-fakturera-som-privatperson',
    '/dashboard': '/instrumentbrada',
    '/insurance': '/forsakring',
    '/health-insurance-main': '/sjukforsakring-huvud',
    '/health-insurance': '/sjukforsakring',
    '/private-healthcare-insurance': '/privatvardsforsakring',
    '/freelance-consultant-salary-invoice-without-a-company': '/frilans-konsult-lon-fakturera-utan-foretag',
    '/invoicing-without-company-2025': '/fakturering-utan-foretag-2025',
    '/working-as-a-freelancer-and-invoicing-without-tax-registration': '/att-jobba-som-frilansare-och-fakturera-utan-fskatt',
    '/how-much-should-I-invoice-as-a-freelancer': '/hur-mycket-ska-jag-fakturera-som-frilansare',
    '/from-invoice-to-negotiation': '/fran-faktura-till-forhandling',
    '/business': '/foretag',
    '/self-employment': '/egenanstallning',
    '/unsubscribed': '/avprenumererad',
    '/authorize-reminder': '/auktorisera-påminnelse',
    '/approval-notify': '/godkannande-meddelande',
    '/freelance-freelancing': '/frilans-frilansarbete',
    '/invoicing-as-an-individual_self-employed': '/fakturera-som-privatperson_egenanstalld',
    '/invoice-without-company-or-business': '/fakturera-utan-foretag-eller-bolag',
    '/invoice-as-consultant-salary': '/fakturera-som-konsult-lon',
    '/news': '/nyheter',
    '/book-keeping': '/bokforing',
    '/freelancing-guide': '/frilansguide',
    '/emailregister': '/e-postregister',
    '/emaillogin': '/e-postinloggning',
    '/forgot-password': '/glomt-ditt-losenord',
    '/login': '/logga-in',
    '/set-new-password': '/set-nytt-losenord',
  }
};

/**
 * Get the equivalent route in another language
 * @param currentPath - The current route path
 * @param fromLang - Current language
 * @param toLang - Target language
 * @returns The equivalent route in the target language or the current path if no mapping exists
 */
export const getEquivalentRoute = (currentPath: string, fromLang: string, toLang: string): string => {
  // Handle root path
  if (currentPath === '/') return '/';

  // Remove query parameters for mapping
  const pathWithoutQuery = currentPath.split('?')[0];

  // Determine mapping direction
  let mappingKey: keyof RouteMappings;
  if (fromLang === LOCALES.SV_SE && toLang === LOCALES.EN_US) {
    mappingKey = 'sv-en';
  } else if (fromLang === LOCALES.EN_US && toLang === LOCALES.SV_SE) {
    mappingKey = 'en-sv';
  } else {
    return currentPath; // No mapping needed for same language
  }

  // Get the mapped route
  const mapping = routeMapping[mappingKey];
  return mapping[pathWithoutQuery] || currentPath;
};

/**
 * Check if a route needs localization
 * @param path - The route path
 * @returns True if the route has language-specific variants
 */
export const isLocalizableRoute = (path: string): boolean => {
  const pathWithoutQuery = path.split('?')[0];
  return Object.values(routeMapping['sv-en']).includes(pathWithoutQuery) ||
    Object.keys(routeMapping['sv-en']).includes(pathWithoutQuery);
};
